import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, TableRow } from '@mui/material';
import { useLocation } from 'react-router';

import { EnrollmentManagementTableRowButtons } from 'offer/components/EnrollmentsManagementTable/EnrollmentManagementTableRowButtons';
import { UserAvatar } from 'user/components/UserAvatar';
import { TableCell } from 'common/components/Table';
import {
    IOfferEventEnrollmentManagementListItemFragment,
    IOfferEventSubscriptionStatus,
    IRole,
    CurrentUserDocument,
} from 'graphql/types';
import { Checkbox } from 'common/components/Checkbox';
import { formatDate, getAbsoluteDate } from 'common/utils/formatDate';
import { EnrollmentsTableActions } from 'offer/components/EnrollmentsTableActions';
import { Link } from 'common/components/Link';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { OfferEventAttendanceChips } from 'offer/components/OfferEventAttendanceChips';
import { ChipGroup } from 'common/components/Chip';

interface IProps {
    enrollment: IOfferEventEnrollmentManagementListItemFragment;
    enrollmentStatus: IOfferEventSubscriptionStatus;
    isSelected: boolean;
    onClickEnroll?(): void;
    onClickReject?(): void;
    onClickDelete?(): void;
    onClickMail?(): void;
    onClickComplete?(): void;
    onClickCertificate?(): void;
    onSelect?: () => void;
}

export const EnrollmentsManagementTableRow = ({
    enrollment,
    enrollmentStatus,
    isSelected,
    onClickEnroll,
    onClickReject,
    onClickDelete,
    onClickMail,
    onClickComplete,
    onClickCertificate,
    onSelect,
}: IProps) => {
    const [translate] = useTranslation();
    const location = useLocation();
    const { openGlobalDrawer } = useGlobalDrawer();
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const currentUser = currentUserData?.currentUser;
    const isManager = currentUser?.roles?.includes(IRole.Manager);

    const { user, offerEvent, statusUpdatedOn, attendance } = enrollment;

    const {
        id,
        title: offerEventTitle,
        offer: { title: offerTitle },
        dates,
        dateCount,
        trainers,
    } = offerEvent;

    const locations = dates
        ?.map((date) => date?.location?.title || date.locationString)
        .filter(Boolean);
    const firstLocation = locations?.[0];

    const date = dates?.[0];
    const { startDate, endDate } = date || {};

    const dateDisplay = date
        ? `${getAbsoluteDate(startDate, 'd MMM yyyy HH:mm')} - ${getAbsoluteDate(endDate, 'd MMM yyyy HH:mm')}`
        : '';

    const moreDatesCount = (dateCount || 0) - 1;
    const moreDates =
        moreDatesCount > 0
            ? translate('enrollmentsManagementTable.moreDates', {
                  count: moreDatesCount,
              })
            : undefined;

    const isRequestStatus =
        enrollmentStatus === IOfferEventSubscriptionStatus.Requested;
    const isWaitingListStatus =
        enrollmentStatus === IOfferEventSubscriptionStatus.Full;
    const isEnrolledStatus =
        enrollmentStatus === IOfferEventSubscriptionStatus.Enrolled;
    const isDeniedStatus =
        enrollmentStatus === IOfferEventSubscriptionStatus.Denied;

    const isOfferEventTrainer = trainers?.edges.some(
        (trainer) => trainer?.node?.id === user.id
    );
    const canChangeAttendance =
        isManager ||
        isOfferEventTrainer ||
        dates?.some(
            (date) =>
                !!date?.trainers?.some(
                    (trainer) => trainer?.id === currentUser?.id
                )
        );

    return (
        <TableRow>
            <TableCell>
                <Checkbox
                    checked={isSelected}
                    onClick={(e) => {
                        onSelect?.();

                        e.stopPropagation();
                    }}
                />
            </TableCell>
            <TableCell
                onClick={() => {
                    openGlobalDrawer({
                        type: IGlobalDrawerType.UserProfile,
                        itemId: user.id,
                    });
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <UserAvatar sizeSm={40} sx={{ mr: 2 }} user={user} />

                    {user.name}
                </Box>
            </TableCell>
            <TableCell>
                <Link
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                        openGlobalDrawer(
                            {
                                type: IGlobalDrawerType.OfferEvent,
                                itemId: id,
                            },
                            location
                        );
                    }}
                >
                    <Typography sx={{ fontWeight: 700 }} variant="body2">
                        {offerEventTitle || offerTitle}
                    </Typography>
                </Link>

                {!!dateDisplay && (
                    <Box sx={{ whiteSpace: 'nowrap' }}>{dateDisplay}</Box>
                )}

                {!!firstLocation && <Box>{firstLocation}</Box>}

                {!!moreDates && <Box>{moreDates}</Box>}
            </TableCell>
            {isDeniedStatus && (
                <TableCell>{enrollment.reason || '-'}</TableCell>
            )}
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {formatDate(statusUpdatedOn, 'd MMMM yyyy')}
            </TableCell>
            {isEnrolledStatus && (
                <TableCell>
                    <ChipGroup>
                        <OfferEventAttendanceChips
                            offerEventDates={offerEvent.dates || []}
                            userAttendance={attendance}
                        />
                    </ChipGroup>
                </TableCell>
            )}
            <TableCell>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box sx={{ ml: 2, display: 'flex' }}>
                        <EnrollmentManagementTableRowButtons
                            enrollmentStatus={enrollmentStatus}
                            onClickEnroll={onClickEnroll}
                            onClickReject={onClickReject}
                        />
                    </Box>

                    <EnrollmentsTableActions
                        enablePortal
                        isWebinarOrMeeting
                        onClickCertificate={
                            isEnrolledStatus ? onClickCertificate : undefined
                        }
                        onClickComplete={
                            isEnrolledStatus && canChangeAttendance
                                ? onClickComplete
                                : undefined
                        }
                        onClickDelete={onClickDelete}
                        onClickEnroll={
                            !isRequestStatus &&
                            !isWaitingListStatus &&
                            !isEnrolledStatus
                                ? onClickEnroll
                                : undefined
                        }
                        onClickMail={onClickMail}
                        onClickReject={
                            !isRequestStatus &&
                            !isWaitingListStatus &&
                            !isDeniedStatus
                                ? onClickReject
                                : undefined
                        }
                    />
                </Box>
            </TableCell>
        </TableRow>
    );
};
